import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Avatar from '../components/ui/Avatar';
import Modal from '../components/ui/Modal';
import { formatPrice, formatNumber, formatTimeAgo, formatDate } from '../utils/formatters';
import { mockComments } from '../data/mockComments';

const DomainDetailPage = () => {
  const { domainName } = useParams();
  const navigate = useNavigate();
  const { getDomainByName, getUserByUsername, currentUser, toggleLike, placeBid, buyDomain, domains } = useApp();
  const { showToast } = useToast();

  const domain = getDomainByName(domainName);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showBidModal, setShowBidModal] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [commentText, setCommentText] = useState('');

  if (!domain) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <div className="text-6xl mb-4">🔍</div>
        <h1 className="text-2xl font-bold mb-4">Domain Not Found</h1>
        <p className="text-gray-600 mb-6">
          The domain you're looking for doesn't exist or has been removed.
        </p>
        <Button onClick={() => navigate('/marketplace')}>
          Browse Marketplace
        </Button>
      </div>
    );
  }

  const isLiked = domain.likedBy?.includes(currentUser.username);
  const seller = getUserByUsername(domain.listedBy);
  const domainComments = mockComments.filter(c => c.domainId === domain.id);
  const similarDomains = domains
    .filter(d => d.category === domain.category && d.id !== domain.id)
    .slice(0, 4);

  const handleBuy = () => {
    buyDomain(domain.id, domain.price);
    showToast('Success! Domain purchased', 'success');
    setShowBuyModal(false);
    navigate('/profile/' + currentUser.username);
  };

  const handlePlaceBid = () => {
    const amount = parseFloat(bidAmount);
    if (amount && amount > 0) {
      placeBid(domain.id, amount);
      showToast(`Bid of ${formatPrice(amount)} placed successfully`, 'success');
      setShowBidModal(false);
      setBidAmount('');
    }
  };

  const SimplePriceChart = ({ priceHistory }) => {
    if (!priceHistory || priceHistory.length === 0) {
      return <div className="h-48 flex items-center justify-center text-gray-400">No price history available</div>;
    }

    const maxPrice = Math.max(...priceHistory.map(p => p.price));
    const minPrice = Math.min(...priceHistory.map(p => p.price));
    const range = maxPrice - minPrice || 1;

    return (
      <div className="h-48 flex items-end gap-2">
        {priceHistory.map((point, index) => {
          const height = ((point.price - minPrice) / range) * 100;
          return (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div
                className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                style={{ height: `${Math.max(height, 10)}%` }}
                title={`${point.date}: ${formatPrice(point.price)}`}
              />
              <p className="text-xs text-gray-500 mt-2">{point.date.split('-')[1]}/{point.date.split('-')[2]}</p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/marketplace')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            ← Back to Marketplace
          </button>

          <div className="flex flex-col md:flex-row items-start justify-between gap-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {domain.name}
              </h1>
              <div className="flex flex-wrap gap-2">
                {domain.badges?.map(badge => (
                  <Badge key={badge} variant="primary">
                    {badge === 'hot' && '🔥 Hot'}
                    {badge === 'new' && '🆕 New'}
                    {badge === 'premium' && '💎 Premium'}
                  </Badge>
                ))}
                <Badge variant="info">
                  Listed {formatTimeAgo(domain.listedAt)}
                </Badge>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => toggleLike(domain.id)}
                className={`p-3 rounded-lg border transition-colors ${
                  isLiked
                    ? 'bg-red-50 border-red-200 text-red-600'
                    : 'hover:bg-gray-100'
                }`}
              >
                {isLiked ? '❤️' : '🤍'} {domain.likes}
              </button>
              <button className="p-3 rounded-lg border hover:bg-gray-100" title="Share">
                🔗
              </button>
              <button className="p-3 rounded-lg border hover:bg-gray-100" title="Report">
                🚩
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Price & Purchase Section */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Current Price</p>
                  <p className="text-4xl font-bold">{formatPrice(domain.price)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600 mb-1">AI Valuation</p>
                  <p className="text-lg font-semibold text-blue-600">
                    {formatPrice(domain.aiValuation.min)} - {formatPrice(domain.aiValuation.max)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {domain.aiValuation.confidence}% confidence
                  </p>
                </div>
              </div>

              {/* Price History Chart */}
              <div className="mb-6">
                <h3 className="font-bold mb-3">Price History</h3>
                <SimplePriceChart priceHistory={domain.priceHistory} />
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {domain.listingType.includes('buyNow') && (
                  <Button
                    fullWidth
                    size="lg"
                    variant="success"
                    onClick={() => setShowBuyModal(true)}
                  >
                    Buy Now for {formatPrice(domain.price)}
                  </Button>
                )}
                {domain.listingType.includes('offers') && (
                  <Button
                    fullWidth
                    size="lg"
                    onClick={() => setShowBidModal(true)}
                  >
                    Make an Offer
                  </Button>
                )}
                {domain.openToTrades && (
                  <Button
                    fullWidth
                    size="lg"
                    variant="outline"
                    onClick={() => setShowTradeModal(true)}
                  >
                    Propose Domain Trade
                  </Button>
                )}
              </div>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 text-center">
                <p className="text-3xl mb-1">👁</p>
                <p className="text-2xl font-bold">{formatNumber(domain.views)}</p>
                <p className="text-sm text-gray-600">Views</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="text-3xl mb-1">❤️</p>
                <p className="text-2xl font-bold">{domain.likes}</p>
                <p className="text-sm text-gray-600">Likes</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="text-3xl mb-1">💬</p>
                <p className="text-2xl font-bold">{domain.comments}</p>
                <p className="text-sm text-gray-600">Comments</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="text-3xl mb-1">📈</p>
                <p className="text-2xl font-bold text-green-600">+15%</p>
                <p className="text-sm text-gray-600">Interest</p>
              </Card>
            </div>

            {/* Seller Information */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">Seller Information</h3>
              <div className="flex items-start gap-4">
                <Avatar src={seller.avatar} size="xl" />
                <div className="flex-1">
                  <Link
                    to={`/profile/${seller.username}`}
                    className="text-xl font-bold hover:text-blue-600"
                  >
                    {seller.displayName}
                  </Link>
                  <p className="text-gray-600">@{seller.username}</p>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-600">Rating</p>
                      <p className="font-bold">⭐ {seller.rating}/5.0</p>
                      <p className="text-xs text-gray-500">
                        ({seller.reviewCount} reviews)
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Sales</p>
                      <p className="font-bold">{seller.stats.totalSales}</p>
                      <p className="text-xs text-gray-500">
                        {formatPrice(seller.stats.totalVolume)} volume
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Success Rate</p>
                      <p className="font-bold">{seller.stats.winRate}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Member Since</p>
                      <p className="font-bold">
                        {formatDate(seller.memberSince, 'short')}
                      </p>
                    </div>
                  </div>

                  {seller.badges && seller.badges.length > 0 && (
                    <div className="flex gap-2 mt-4">
                      {seller.badges.map(badge => (
                        <Badge key={badge} variant="success" size="sm">
                          {badge === 'topTrader' && '🏆 Top Trader'}
                          {badge === 'speedDemon' && '⚡ Speed Demon'}
                          {badge === 'eliteMember' && '💎 Elite'}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <Button className="mt-4" size="sm">
                    Follow
                  </Button>
                </div>
              </div>
            </Card>

            {/* Current Bids */}
            {domain.bids && domain.bids.length > 0 && (
              <Card className="p-6">
                <h3 className="font-bold mb-4">Current Bids ({domain.bids.length})</h3>
                <div className="space-y-3">
                  {domain.bids.map((bid, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Avatar src={`https://i.pravatar.cc/150?img=${index + 5}`} size="sm" />
                        <div>
                          <p className="font-medium">@{bid.username}</p>
                          <p className="text-xs text-gray-600">
                            {formatTimeAgo(bid.timestamp)}
                          </p>
                        </div>
                      </div>
                      <p className="text-lg font-bold">{formatPrice(bid.amount)}</p>
                    </div>
                  ))}
                </div>
                <Button fullWidth className="mt-4" onClick={() => setShowBidModal(true)}>
                  Place Your Bid
                </Button>
              </Card>
            )}

            {/* Trade Offers */}
            {domain.openToTrades && (
              <Card className="p-6">
                <h3 className="font-bold mb-4">🔄 Open to Domain Swaps</h3>
                <p className="text-gray-600 mb-4">
                  Seller will consider trading for:
                </p>
                <p className="mb-4 font-medium">{domain.tradePreferences}</p>
                <Button fullWidth onClick={() => setShowTradeModal(true)}>
                  Propose Trade
                </Button>
              </Card>
            )}

            {/* Domain Details */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">Domain Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Registered</p>
                  <p className="font-medium">{domain.registrationInfo.registered}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Expires</p>
                  <p className="font-medium">{domain.registrationInfo.expires}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">TLD</p>
                  <p className="font-medium">.{domain.tld}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Length</p>
                  <p className="font-medium">{domain.length} characters</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Category</p>
                  <p className="font-medium capitalize">{domain.category}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Registrar</p>
                  <p className="font-medium">{domain.registrationInfo.registrar}</p>
                </div>
              </div>
            </Card>

            {/* SEO Metrics */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">SEO Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Search Volume</p>
                  <p className="font-bold">
                    {formatNumber(domain.seoMetrics.searchVolume)}/month
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Domain Authority</p>
                  <p className="font-bold">{domain.seoMetrics.domainAuthority}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Backlinks</p>
                  <p className="font-bold">{domain.seoMetrics.backlinks}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Keywords</p>
                  <div className="flex flex-wrap gap-1">
                    {domain.seoMetrics.keywords.map(keyword => (
                      <Badge key={keyword} size="sm">{keyword}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* AI Analysis */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">🤖 AI Analysis Breakdown</h3>
              <div className="space-y-4">
                {[
                  { label: 'Brandability', value: domain.aiAnalysis.brandability, desc: 'Excellent' },
                  { label: 'Memorability', value: domain.aiAnalysis.memorability, desc: 'Very Good' },
                  { label: 'Keyword Value', value: domain.aiAnalysis.keywordValue, desc: 'High' },
                  { label: 'TLD Premium', value: domain.aiAnalysis.tldPremium, desc: `.${domain.tld} is valuable` },
                  { label: 'Market Trend', value: domain.aiAnalysis.marketTrend, desc: 'Growing' },
                ].map(item => (
                  <div key={item.label}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{item.label}</span>
                      <span className="text-sm text-gray-600">{item.desc}</span>
                    </div>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-2 flex-1 rounded ${
                            i < item.value ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-1">Overall Score</p>
                <p className="text-3xl font-bold text-blue-600">
                  {domain.aiAnalysis.overallScore}/100
                </p>
              </div>
            </Card>

            {/* Similar Domains */}
            <div>
              <h3 className="text-2xl font-bold mb-4">You Might Also Like</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {similarDomains.map(similarDomain => (
                  <Card
                    key={similarDomain.id}
                    hover
                    className="p-4 cursor-pointer"
                    onClick={() => navigate(`/domain/${similarDomain.name}`)}
                  >
                    <h4 className="font-bold mb-2">{similarDomain.name}</h4>
                    <p className="text-2xl font-bold text-blue-600 mb-2">
                      {formatPrice(similarDomain.price)}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>👁 {formatNumber(similarDomain.views)}</span>
                      <span>❤️ {similarDomain.likes}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Social & Activity */}
          <div className="space-y-6">
            {/* Comments Section */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">💬 {domain.comments} Comments</h3>

              {/* Comment input */}
              <div className="mb-6">
                <div className="flex gap-3">
                  <Avatar src={currentUser.avatar} size="sm" />
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Add a comment..."
                      className="w-full px-4 py-2 border rounded-lg mb-2"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                    />
                    <Button size="sm" disabled={!commentText}>
                      Post
                    </Button>
                  </div>
                </div>
              </div>

              {/* Comment list */}
              <div className="space-y-4">
                {domainComments.map(comment => (
                  <div key={comment.id} className="flex gap-3">
                    <Avatar src={comment.avatar} size="sm" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">@{comment.username}</p>
                      <p className="text-sm text-gray-600 mt-1">{comment.text}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <button className="hover:text-red-600">
                          ❤️ {comment.likes}
                        </button>
                        <span>{formatTimeAgo(comment.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Activity Feed */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">📊 Recent Activity</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-orange-500">📈</span>
                  <div>
                    <p>Became trending</p>
                    <p className="text-xs text-gray-500">1 hour ago</p>
                  </div>
                </div>
                {domain.bids && domain.bids.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className="text-green-500">💰</span>
                    <div>
                      <p>@{domain.bids[0].username} bid {formatPrice(domain.bids[0].amount)}</p>
                      <p className="text-xs text-gray-500">{formatTimeAgo(domain.bids[0].timestamp)}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <span className="text-red-500">❤️</span>
                  <div>
                    <p>Liked by @user5 and {domain.likes - 1} others</p>
                    <p className="text-xs text-gray-500">3 hours ago</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Price Alerts */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">🔔 Price Alerts</h3>
              <p className="text-sm text-gray-600 mb-4">
                Get notified when price changes
              </p>
              <input
                type="email"
                placeholder="Your email"
                className="w-full px-4 py-2 border rounded-lg mb-3"
              />
              <Button fullWidth>Set Alert</Button>
              <p className="text-xs text-gray-500 mt-3 text-center">
                {formatNumber(241)} traders watching
              </p>
            </Card>

            {/* Share */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">🔗 Share This Domain</h3>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm">
                  Twitter
                </Button>
                <Button variant="outline" size="sm">
                  LinkedIn
                </Button>
                <Button variant="outline" size="sm">
                  Facebook
                </Button>
                <Button variant="outline" size="sm">
                  Copy Link
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Buy Modal */}
      <Modal isOpen={showBuyModal} onClose={() => setShowBuyModal(false)} title="Confirm Purchase">
        <div className="p-6">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold mb-2">{domain.name}</h3>
            <p className="text-4xl font-bold text-blue-600">{formatPrice(domain.price)}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Domain Price:</span>
              <span className="font-bold">{formatPrice(domain.price)}</span>
            </div>
            <div className="flex justify-between">
              <span>Transaction Fee (8%):</span>
              <span className="font-bold">{formatPrice(domain.price * 0.08)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-bold">Total:</span>
              <span className="font-bold text-blue-600">{formatPrice(domain.price * 1.08)}</span>
            </div>
          </div>
          <div className="flex gap-3">
            <Button fullWidth variant="outline" onClick={() => setShowBuyModal(false)}>
              Cancel
            </Button>
            <Button fullWidth variant="success" onClick={handleBuy}>
              Confirm Purchase
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bid Modal */}
      <Modal isOpen={showBidModal} onClose={() => setShowBidModal(false)} title="Make an Offer">
        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Asking Price: {formatPrice(domain.price)}</p>
            {domain.bids && domain.bids.length > 0 && (
              <p className="text-sm text-gray-600">Highest Bid: {formatPrice(domain.bids[0].amount)}</p>
            )}
          </div>
          <input
            type="number"
            placeholder="Enter your offer amount"
            className="w-full px-4 py-3 border rounded-lg mb-4"
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
          />
          <div className="flex gap-3">
            <Button fullWidth variant="outline" onClick={() => setShowBidModal(false)}>
              Cancel
            </Button>
            <Button fullWidth onClick={handlePlaceBid} disabled={!bidAmount}>
              Place Bid
            </Button>
          </div>
        </div>
      </Modal>

      {/* Trade Modal */}
      <Modal isOpen={showTradeModal} onClose={() => setShowTradeModal(false)} title="Propose Domain Trade">
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Seller preferences: {domain.tradePreferences}
          </p>
          <select className="w-full px-4 py-3 border rounded-lg mb-4">
            <option>Select your domain to trade...</option>
            <option>MyStartup.com ({formatPrice(5200)})</option>
            <option>TechVenture.io ({formatPrice(4800)})</option>
          </select>
          <textarea
            placeholder="Add a message to the seller..."
            className="w-full px-4 py-3 border rounded-lg mb-4 h-24"
          />
          <div className="flex gap-3">
            <Button fullWidth variant="outline" onClick={() => setShowTradeModal(false)}>
              Cancel
            </Button>
            <Button fullWidth>
              Propose Trade
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DomainDetailPage;
